// MegaMenuWrapper.tsx
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/enhanced-mega-menu.css';
// The menu component is plain JS shared byte-for-byte with the Shopify theme
// (theme: assets/enhanced-mega-menu-component.js). Edit it there, copy it here.
// @ts-ignore -- untyped JS module
import createMegaMenu from '../lib/enhanced-mega-menu-component.js';

declare global {
  interface Window {
    React?: typeof React;
    megaMenuData?: any;
    customMenuData?: any;
    MegaMenu?: any;
  }
}

// The menu data is shared verbatim with the Shopify theme, where link targets
// are relative to the storefront. This app can be served from another origin
// (client.trip-planner.com.au), so the component prefixes every relative link
// with the store's origin. It also only fetches live product counts when the
// page is on the store's own domain (/apps/campsite-explorer); elsewhere it
// shows the count snapshot from the data file.
const STORE_ORIGIN = 'https://campingaustralia.com.au';

// Module-level: start loading the data as soon as this runs in the browser.
if (typeof window !== 'undefined') {
  import('../lib/enhanced-mega-menu-data.js').catch(() => {});
}

function readMenuData(): any[] {
  if (typeof window !== 'undefined') {
    if (window.customMenuData?.data?.megamenu) return window.customMenuData.data.megamenu;
    if (window.megaMenuData?.data?.megamenu) return window.megaMenuData.data.megamenu;
  }
  return [];
}

export const MegaMenuWrapper: React.FC = () => {
  // IMPORTANT: start empty so the first client render matches the server
  // (which has no window menu data). Reading window data in a lazy
  // initializer caused a hydration mismatch (React #418/#423) that tore down
  // the whole SSR tree. The menu is populated after mount instead.
  const [menuData, setMenuData] = useState<any[]>([]);

  useEffect(() => {
    const existing = readMenuData();
    if (existing.length > 0) {
      setMenuData(existing);
    } else {
      import('../lib/enhanced-mega-menu-data.js')
        .then(() => {
          const data = readMenuData();
          if (data.length > 0) setMenuData(data);
        })
        .catch(() => {});
    }
    if (typeof window !== 'undefined') window.React = React;
  }, []);

  const Menu = useMemo(() => createMegaMenu(React) as React.FC<{ menuData: any[]; storeOrigin: string }>, []);

  // With no data yet it renders an empty bar, so there's no layout shift.
  return <Menu menuData={menuData} storeOrigin={STORE_ORIGIN} />;
};
